import React, { ReactNode } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { Heading } from '../Heading';
import { DeleteIcon, EditOutline, MoreOutline } from '../Icons';

import { useTranslation } from 'react-i18next';
import '@/styles/style.css';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from '@/config';
import { OrderStatusTab } from '@/types';
import { useHandleCancelOrder } from '@/hooks';
import { ItemType } from 'antd/es/menu/interface';
import { formatExportCount } from '@/utils/helpers/orderExport';

interface BottomSheetProps {
  withoutButton?: boolean;
  isVisible: boolean;
  Icon?: ReactNode;
  numberSelected?: number;
  total?: number;
  labelBtn?: string;
  children?: ReactNode;
  showRecords?: boolean;
  justify?: string;
  menuItems?: MenuProps['items'];
  onSubmit?: () => void;
  loadingSubmit?: boolean;
  submitDisabled?: boolean;
  uid?: string;
  editDisabled?: boolean;
  deleteLabel?: string;
  activeTab?: OrderStatusTab;
  cancelDisabled?: boolean;
  disableDropdown?: boolean;
  onClickDropdown?: any;
  //
  customMenuItem?: CustomMenuItemData[];
  isAllSelected?: boolean;
  toggleIsAllSelected?: () => void;
  shouldHaveSelectAll?: boolean;
  additionalContent?: ReactNode;
  justShowAdditionalContent?: boolean;
  sticky?: boolean;
  wrapperClassName?: string;
}

interface CustomMenuItemData {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  leadingIcon?: ReactNode;
  loading?: boolean;
  key: string;
}


const BottomSheet: React.FC<BottomSheetProps> = ({
  withoutButton = false,
  isVisible,
  numberSelected = 0,
  total = 0,
  labelBtn = 'ship now',
  Icon,
  children,
  showRecords = true,
  menuItems,
  onSubmit,
  uid,
  editDisabled = false,
  cancelDisabled = false,
  disableDropdown = false,
  deleteLabel,
  loadingSubmit,
  submitDisabled = false,
  activeTab,
  justify = 'flex-end',
  //
  customMenuItem,
  isAllSelected,
  toggleIsAllSelected,
  shouldHaveSelectAll,
  additionalContent,
  justShowAdditionalContent,
  sticky = false,
  wrapperClassName,
}) => {
  const bulkAnimClass = isVisible ? 'bulk-action-animate-in' : 'bulk-action-animate-out';
  const heightClass = isVisible ? 'h-[90px] max-h-[100px] p-4' : 'h-0 max-h-0 pointer-events-none';
  const positionClass = 'relative';
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { handleCancelOrder, isLoading } = useHandleCancelOrder(
    uid || '',
    activeTab || OrderStatusTab.READY_TO_SHIP,
  );

  const dropDownDividerItem: ItemType = {
    type: 'divider',
  };

  const getCustomMenuItems = (customMenuItems?: CustomMenuItemData[]): ItemType[] => {
    const items: ItemType[] = [];
    customMenuItems?.forEach((item, index) => {
      items.push({
        key: item.key,
        label: (
          <Heading
            title={item.label}
            loading={item.loading}
            leadingIcon={item.leadingIcon}
            disabled={item.disabled}
          />
        ),
        onClick: item.onClick,
      });
      if (index !== customMenuItems.length - 1) items.push(dropDownDividerItem);
    });

    return items ?? [];
  };
  const items: MenuProps['items'] = customMenuItem
    ? getCustomMenuItems(customMenuItem)
    : menuItems || [
        {
          key: '1',
          label: (
            <Heading
              title={t('edit')}
              leadingIcon={<EditOutline color={editDisabled ? '#ccc' : '#38383e'} />}
              rotation={false}
              disabled={editDisabled}
            />
          ),
          disabled: editDisabled,
          onClick: () => navigate(ROUTE.ORDER_EDIT.replace(':orderUid', String(uid))),
        },
        {
          type: 'divider',
        },
        {
          key: '2',
          label: (
            <Heading
              title={deleteLabel ?? t('cancel')}
              leadingIcon={<DeleteIcon color={cancelDisabled ? '#ccc' : '#38383e'} />}
              rotation={false}
              loading={isLoading}
              disabled={cancelDisabled}
            />
          ),
          disabled: cancelDisabled,
          onClick: handleCancelOrder,
        },
      ];

  return (
    <div
      className={`${positionClass} w-full overflow-hidden ${heightClass} gap-4 flex items-center border-solid border border-[rgb(232,230,227)] rounded-2xl shadow-lg bg-white ${bulkAnimClass} ${wrapperClassName ?? ''}`}
      style={{ justifyContent: justify, transitionProperty: 'transform, max-height' }}
    >
      {children ? (
        children
      ) : (
        <>
          {justShowAdditionalContent ? (
            additionalContent
          ) : (
            <>
              {additionalContent}
              {showRecords && (
                <span className='text-sm font-medium text-[rgb(57,57,60)]'>
                  {formatExportCount(numberSelected, i18n.language)}{' '}
                  <span className='font-normal text-[rgb(115,123,140)]'>{t('order_selected')}</span>
                </span>
              )}
              {shouldHaveSelectAll && total > 1 && numberSelected !== total && (
                <button
                  type='button'
                  className='text-sm font-medium cursor-pointer text-[rgb(59,130,246)] hover:text-[rgb(37,99,235)] transition-colors'
                  onClick={toggleIsAllSelected}
                >
                  {isAllSelected
                    ? t('clear_all')
                    : t('select_all_orders', {
                        order_count: formatExportCount(total, i18n.language),
                      })}
                </button>
              )}
              {!withoutButton && (
                <button
                  type='button'
                  className='flex items-center gap-1.5 h-10 px-5 min-w-[150px] rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-sm font-bold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
                  onClick={onSubmit}
                  disabled={loadingSubmit || submitDisabled}
                >
                  {loadingSubmit ? (
                    <span className='h-4 w-4 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                  ) : (
                    Icon
                  )}
                  {labelBtn}
                </button>
              )}
              {!disableDropdown && (
                <Dropdown
                  menu={{ items }}
                  trigger={['click']}
                  rootClassName='bottomSheet'
                  placement='topLeft'
                >
                  <button
                    type='button'
                    className='flex items-center justify-center w-10 h-10 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
                  >
                    <MoreOutline width={20} height={20} />
                  </button>
                </Dropdown>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default BottomSheet;
